import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

// Constants and Utils
import {
  ImageFileTypes,
  ProgressTexts,
} from "../../constants/GeneralConstants";
import { selectFile } from "../../common-utils/file-utils/FileUtils";
import { toLocaleString } from "../../common-utils/string-utils/StringUtils";

// Components
import { Progress } from "../progress-status/Progress";

// this returns the "success/error" message after uploading excel-file
function SuccessAndErrorStatus({ mediaCount, excelErrors = {} }) {
  const excelErrorKeys = Object.keys(excelErrors);

  if (mediaCount) {
    return (
      <p className="text-success">
        <i className="fa fa-check-circle" aria-hidden="true"></i> {mediaCount}{" "}
        sites successfully uploaded.
      </p>
    );
  }

  if (excelErrorKeys.length > 0) {
    return (
      <div className="text-danger">
        <p className="mb-1 font-weight-bold">
          <i className="fa fa-exclamation-circle" aria-hidden="true"></i>{" "}
          {excelErrorKeys.length} errors found. Please fix them and uploading
          again. Errors are listed below
        </p>

        {/* Errors */}
        <ol className="overflow-auto max-h-300 mr-5">
          {excelErrorKeys.map((errorKey, i) => {
            return (
              <li key={i} className="mb-0">
                {errorKey} {"-->"} {excelErrors[errorKey]}
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  return null;
}

function UploadTemplateCard({
  uploadSiteListData,
  reducerKeyValuesObj,
  uploadTemplateFunction,
  isUploadTemplateButtonDisabled = false,
  isImageUploadRequired = true,
}) {
  // State
  const [imageFiles, setImageFiles] = useState([]);
  const [imageFilesCount, setImageFilesCount] = useState(0);

  const { step, title, message, buttonLabel } = uploadSiteListData || {};
  const uploadProgressTexts = ProgressTexts.Uploads;

  // reducer states for upload-component
  const { reducerKey, selectorValue } = reducerKeyValuesObj;

  const loading = useSelector(
    (state) => state[reducerKey][selectorValue.loading]
  );
  const excelErrors = useSelector(
    (state) => state[reducerKey][selectorValue.error]
  );
  const mediaCount = useSelector(
    (state) => state[reducerKey][selectorValue.mediaCount]
  );

  // function
  async function onUploadImagesClick() {
    const files = await selectFile(true, ImageFileTypes);
    const filesCount = files.length;

    // no files selected
    if (!filesCount) {
      toast.error("No image selected");
      return;
    }

    // update states
    setImageFilesCount(filesCount);
    setImageFiles(files);
  }

  // selected images message
  const selectedImagesCount = imageFilesCount
    ? `(${toLocaleString(imageFilesCount)})`
    : "";

  return (
    <div className="card shadow mb-4 mt-3">
      <div className="d-flex justify-content-between">
        <div className="card-content">
          <p className="card__step">{step}</p>
          <p className="card__title">{title}</p>
          <p className="card__message">{message}</p>
          <SuccessAndErrorStatus
            mediaCount={mediaCount}
            excelErrors={excelErrors}
          />
        </div>
        {loading ? (
          <Progress className="mr-4" progressTexts={uploadProgressTexts} />
        ) : (
          <div className="card-action">
            <div className="d-flex flex-column">
              {/* Images */}
              {isImageUploadRequired && (
                <button
                  className="btn btn-primary btn-action mt-2"
                  onClick={onUploadImagesClick}
                  disabled={isUploadTemplateButtonDisabled}
                >
                  {`Select Images ${selectedImagesCount}`}
                </button>
              )}

              {/* Excel */}
              <button
                className="btn btn-primary btn-action mt-3"
                onClick={() => uploadTemplateFunction(imageFiles)}
                disabled={isUploadTemplateButtonDisabled}
              >
                {buttonLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadTemplateCard;
